const content = `
# Component Models

Component models predict building electrical load by disaggregating it into four physical end-use categories and modeling each independently with LightGBM.

## Why Disaggregate?

A building's total electrical load is the sum of distinct physical systems, each driven by different factors:

- **HVAC** responds primarily to outdoor temperature and humidity
- **Refrigeration** responds to ambient temperature and door openings
- **Plug loads** follow occupancy and business schedules
- **Lighting** follows occupancy with daylight modulation

A single "total load" model must learn all these relationships simultaneously. Disaggregated models let each component focus on its physical driver, leading to better generalization -- especially at cold-start when there is no building-specific history to learn from.

## LightGBM

LightGBM is a gradient boosting decision tree framework. It learns by:

1. Fitting a simple tree to the residual error
2. Adding that tree to the ensemble (with a learning rate)
3. Repeating for hundreds of rounds

LightGBM is fast, handles missing values, and naturally captures nonlinear relationships like the temperature-HVAC curve (S-shaped: minimal below 55F, steep above 75F, saturating above 95F).

## Feature Engineering

Each component model receives the same feature set, but learns to weight features differently:

| Feature Category | Features | Primary Driver For |
|-----------------|----------|-------------------|
| **Weather** | Temperature, humidity, irradiance, wind speed | HVAC, refrigeration |
| **Calendar** | Hour, day of week, month, holiday flag | Plug loads, lighting |
| **Building** | Type, sqft, vintage, stories, climate zone | All components |
| **Derived** | Cooling degree hours, heating degree hours, is_daytime | HVAC |

## Per-Building-Type Clusters

Buildings of the same type (e.g., "LargeOffice") can have very different load shapes depending on HVAC system, vintage, and operational patterns. To capture this variation:

1. **Flatten** each building's 8760-hour profile into a feature vector
2. **Cluster** using KMeans into 4 archetypes per building type
3. **Train separate models** for each component x type x cluster

This means a "LargeOffice" cold-start prediction first assigns the building to its nearest cluster, then uses the cluster-specific models.

## Typical Accuracy

| Component | MAPE Target | R-squared Target |
|-----------|------------|-----------------|
| HVAC | < 20% | > 0.80 |
| Refrigeration | < 15% | > 0.85 |
| Plug Loads | < 12% | > 0.85 |
| Lighting | < 15% | > 0.80 |

HVAC is hardest to predict because it depends heavily on the specific HVAC system and building envelope, which cold-start metadata only partially describes.

## Fine-Tuning

After a building has 3-7 days of real meter data, fine-tuning adjusts the cold-start prediction:

1. Compute residuals between prediction and actual for each component
2. Fit a correction model on the residuals using the same features
3. Add the correction to future predictions

This typically reduces MAPE by 5-10 percentage points and brings PoP from 85% to 90%+.
`;

export default content;
